import { isPlatformBrowser } from "@angular/common";
import { Inject, inject, PLATFORM_ID } from "@angular/core";
import { Injectable, signal } from "@angular/core";
import { AngularFireFunctions } from "@angular/fire/compat/functions";
import {
  collection,
  doc,
  Firestore,
  getDocs,
  getDoc,
  onSnapshot,
  query,
  setDoc,
  updateDoc,
  where,
} from "@angular/fire/firestore";

import { MatBottomSheet } from "@angular/material/bottom-sheet";
import { MatDialog } from "@angular/material/dialog";
import { ActivatedRoute, Data, Router } from "@angular/router";
import { AuthService } from "@core/auth.service";
import { AuthStateService } from "@core/auth-state.service";
import { CatchErrorService } from "@core/catch-error.service";
import { ErrorHandlerService } from "@core/ErrorHandlerService/error-handler.service";
import { IndexedDBRecoveryService } from "@core/indexeddb-recovery.service";
import { getFunctions, httpsCallable } from "firebase/functions";
import {
  BehaviorSubject,
  catchError,
  defer,
  delay,
  filter,
  forkJoin,
  from,
  map,
  Observable,
  of,
  ReplaySubject,
  retryWhen,
  scan,
  shareReplay,
  switchMap,
  take,
  timeout,
  throwError,
  timer,
} from "rxjs";
import { CommonVariable } from "src/app/app.constants";
import { InboxTemplateComponent } from "src/app/components/inbox-template/inbox-template.component";
import { InformStripComponent } from "src/app/components/inform-strip/inform-strip.component";
import { NotificationPopComponent } from "src/app/components/notification-pop/notification-pop.component";
import { StandardPromotionComponent } from "src/app/components/standard-promotion/standard-promotion.component";
import { SugerDropComponent } from "src/app/components/suger-drop/suger-drop.component";
import { TakeoverPageComponent } from "src/app/components/takeover-page/takeover-page.component";
import { TopNotificationComponent } from "src/app/components/top-notification/top-notification.component";
import { AccountRepositoryService } from "src/app/pages/account/account-repository.service";
import { CommonService } from "src/app/services/common/common.service";
import { RequestBodyService } from "src/app/services/request-body.service";
import { SafeLocalStorageService } from "src/app/services/safe-local-storage.service";

@Injectable({
  providedIn: "root",
})
export class AuthPartialRegService {
  private safeLocalStorage = inject(SafeLocalStorageService);
  protected selectedlanguage = signal(this.safeLocalStorage.getItem("language_code"));
  prosub: any;
  public autoUserId: string;
  selectedCurrency: string = "GBP";
  truevoDetails$: BehaviorSubject<Data> = new BehaviorSubject({});
  userRequest$ = new ReplaySubject(1);
  selectedlanguage$ = new BehaviorSubject(this.safeLocalStorage.getItem("language_code"));
  public customCheck: boolean = true;
  freebieVoucherAssigned: boolean;
  giftCard: string;
  public autoShowInboxSubject = new BehaviorSubject<boolean | null>(null);
  isPushSignalsActive = signal<boolean>(false);
  errorHandler = inject(ErrorHandlerService);
  private indexedDBRecovery = inject(IndexedDBRecoveryService);

  /**
   * How long partial_Reg will wait for a uid before sending the request
   * without one. Short because the value is optional — see partial_Reg.
   */
  private static readonly UID_BEST_EFFORT_MS = 1000;
  /** Cap wait for account-level push free-lines issued flag (new-device banner). */
  private static readonly PUSH_ISSUED_FLAG_TIMEOUT_MS = 5000;

  /**
   * In-flight / completed full partial_Reg for the active sales journey.
   * Started at Join Now so Account Created → 4C can reuse the result instead of
   * paying for partialregv2 again on the critical path.
   */
  private journeyPartialRegPrefetchKey: string | null = null;
  private journeyPartialRegPrefetch$: Observable<any> | null = null;

  tp1 = null;
  tp2 = null;
  tp3 = null;
  other = null;

  constructor(
    public angularFireFunctions: AngularFireFunctions,
    private catchService: CatchErrorService,
    public requestBodyService: RequestBodyService,
    private matDialog: MatDialog,
    private bottomSheet: MatBottomSheet,
    private firestore: Firestore,
    private accountService: AccountRepositoryService,
    private authService: AuthService,
    private authStateService: AuthStateService,
    private commonService: CommonService,
    private route : ActivatedRoute,
    private router: Router,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {
    this.getCurrencyAndLanguageList$.subscribe();
    this.getActiveCurrencyList$?.subscribe();
    this.freebieVoucherAssigned = false;
    // Defer the signal write into a new event-loop task so that calling
    // selectedlanguage$.next() during component initialization (which is
    // synchronous on BehaviorSubjects) does not add extra stack depth in the
    // same call frame. This is specifically required to avoid "RangeError:
    // Maximum call stack size exceeded" on WebKit (iPhone / Chrome Mobile iOS)
    // where the call stack limit is shallower than V8.
    // Capture the resolved value before scheduling so a later emission cannot
    // change the fallback, and skip no-op writes to avoid feedback loops.
    this.selectedlanguage$.subscribe((res) => {
      const value = res ?? this.selectedlanguage$.value;
      if (value === this.selectedlanguage()) {
        return;
      }
      setTimeout(() => {
        this.selectedlanguage.set(value);
      }, 0);
    });

    const queryParams = this.route.snapshot.queryParams;
    this.tp1 = queryParams.tp1 ?? null;
    this.tp2 = queryParams.tp2 ?? null;
    this.tp3 = queryParams.tp3 ?? null;
    this.other = queryParams.other ?? null;
  }
  /**
   * Warm the full partial_Reg response during registration (overlaps Twilio/SQL/Firebase).
   * Account Created / Payment reuse this via getJourneyPartialRegPrefetch().
   */
  startJourneyPartialRegPrefetch(prosub_id: string | null | undefined): void {
    if (!prosub_id) {
      return;
    }
    const key = String(prosub_id);
    if (this.journeyPartialRegPrefetchKey === key && this.journeyPartialRegPrefetch$) {
      return;
    }
    this.journeyPartialRegPrefetchKey = key;
    this.journeyPartialRegPrefetch$ = this.partial_Reg(prosub_id, "", "", "full").pipe(
      take(1),
      shareReplay({ bufferSize: 1, refCount: false })
    );
    this.journeyPartialRegPrefetch$.subscribe({
      error: () => {
        if (this.journeyPartialRegPrefetchKey === key) {
          this.journeyPartialRegPrefetchKey = null;
          this.journeyPartialRegPrefetch$ = null;
        }
      },
    });
  }

  getJourneyPartialRegPrefetch(prosub_id: string | null | undefined): Observable<any> | null {
    if (!prosub_id || this.journeyPartialRegPrefetchKey !== String(prosub_id)) {
      return null;
    }
    return this.journeyPartialRegPrefetch$;
  }

  // This function is used for partial registration functionalities
  partial_Reg(prosub_id, affiliateId, campaignId, responseMode: "route" | "landing" | "full" = "full",
    locale?: { language: string; currency: string }) {
    let tp1 =
      this.prosub && this.prosub.params && this.prosub.params.TP1
        ? this.prosub.params.TP1?.toLowerCase()
        : this.safeLocalStorage.getItem("TP1")
          ? this.safeLocalStorage.getItem("TP1")
          : this.getCookie("TP1")
            ? this.getCookie("TP1")
            : "";

    let tp2 =
      this.prosub && this.prosub.params && this.prosub.params.TP2
        ? this.prosub.params.TP2?.toLowerCase()
        : this.safeLocalStorage.getItem("TP2")
          ? this.safeLocalStorage.getItem("TP2")
          : this.getCookie("TP2")
            ? this.getCookie("TP2")
            : "";

    let tp3 =
      this.prosub && this.prosub?.params
        ? this.prosub?.params?.TP3
          ? this.prosub.params.TP3?.toLowerCase()
          : this.prosub.params[
            Object.keys(this.prosub.params)?.find((key) => key?.toLowerCase() === "tp3")
          ]?.toLowerCase() || this.getDataFromLocalStorageOrCookie("TP3")
        : this.getDataFromLocalStorageOrCookie("TP3");

    let t_id =
      this.prosub && this.prosub?.params
        ? this.prosub?.params?.T_ID
          ? this.prosub.params.T_ID?.toLowerCase()
          : this.prosub.params[
            Object.keys(this.prosub.params)?.find((key) => key?.toLowerCase() === "t_id")
          ]?.toLowerCase() || this.getDataFromLocalStorageOrCookie("T_ID")
        : this.getDataFromLocalStorageOrCookie("T_ID");

    let other =
      this.prosub && this.prosub.params && this.prosub.params.Other
        ? this.prosub.params.Other?.toLowerCase()
        : this.safeLocalStorage.getItem("Other")
          ? this.safeLocalStorage.getItem("Other")
          : this.getCookie("Other")
            ? this.getCookie("Other")
            : "";

    // ✅ Return the observable so caller can await/subscribe
    //
    // The uid is best-effort here and deliberately not waited on for long:
    // partialregv2 validates the `userId` field and then ignores it, deriving
    // identity from the server-trusted callable `auth` context instead (and
    // httpsCallable attaches the Firebase token regardless of whether we
    // resolved a uid first). Blocking the registration render on a value the
    // backend discards is what let a stalled auth gate hold the form back.
    return this.commonService.getUid("partial_Reg").pipe(
      timeout({ each: AuthPartialRegService.UID_BEST_EFFORT_MS, with: () => of(null) }),
      switchMap((res) => {
        const userId = res || this.autoUserId;

        return this.getV2CloudFunction("partialregv2", {
          prosub_id: prosub_id,
          userId: userId,
          preferred_currency: locale?.currency || this.safeLocalStorage.getItem("user_currency") || this.selectedCurrency,
          response_mode: responseMode,
          Other: other,
          TP1: tp1,
          TP2: tp2,
          TP3: tp3,
          T_ID: t_id,
          affiliateId: affiliateId,
          campaignId: campaignId,
          gc: this.giftCard,
          language_code: locale?.language || this.selectedlanguage() || "en",
        });
      }),
      catchError((err) => {
        return throwError(() => new Error(`Partial Registration Error: ${err}`));
      })
    );
  }

  // Get cookie value of given name
  getCookie(name) {
    try {
      var nameEQ = name + "=";
      var cookie = document.cookie?.split(";");
      for (var i = 0; i < cookie?.length; i++) {
        var c = cookie[i];
        while (c.charAt(0) == " ") c = c?.substring(1, c?.length);
        if (c?.indexOf(nameEQ) == 0) return c?.substring(nameEQ?.length, c?.length);
      }
      return null;
    } catch (e) {
      return e;
    }
  }

  private getDataFromLocalStorageOrCookie(keyName: string) {
    return this.safeLocalStorage.getItem(keyName)
      ? this.safeLocalStorage.getItem(keyName)
      : this.getCookie(keyName)
        ? this.getCookie(keyName)
        : "";
  }

  //  we alrady move
  public getV2CloudFunction(CloudFunctionName: string, payload: Data | string, options: Data = {}) {
    try {
      const functions = getFunctions();
      functions.region = AuthService.FIREBASE_REGION;
      return defer(() => {
        return from(httpsCallable(functions, CloudFunctionName, options)(payload));
      }).pipe(
        map((res: Data) => {
          return res?.data;
        }),
        retryWhen((errors) =>
          errors.pipe(
            scan((retryCount, error) => {
              if (retryCount >= 3) {
                throw error;
              }
              return retryCount + 1;
            }, 0),
            delay(1000) // Delay for retry in milliseconds
          )
        ),
        catchError((error) => {
          const errorMessage = error?.message || error?.toString() || String(error);
          return throwError(new Error(`Error in ${CloudFunctionName}, Reason: ${errorMessage}`));
        })
      );
    } catch (error) {
      return throwError(error);
    }
  }

  // This method is used for 4g flow functionalities
  check4gFlow(prosub_id) {
    return this.authService.getUid$("AuthPartialRegService").pipe(
      switchMap((userId) => {
        let tp1 =
          this.prosub && this.prosub.params && this.prosub.params.TP1
            ? this.prosub.params.TP1?.toLowerCase()
            : this.safeLocalStorage.getItem("TP1")
              ? this.safeLocalStorage.getItem("TP1")
              : this.getCookie("TP1")
                ? this.getCookie("TP1")
                : "";
        let tp2 =
          this.prosub && this.prosub.params && this.prosub.params.TP2
            ? this.prosub.params.TP2?.toLowerCase()
            : this.safeLocalStorage.getItem("TP2")
              ? this.safeLocalStorage.getItem("TP2")
              : this.getCookie("TP2")
                ? this.getCookie("TP2")
                : "";
        let tp3 =
          this.prosub && this.prosub?.params
            ? this.prosub?.params?.TP3
              ? this.prosub.params.TP3?.toLowerCase()
              : this.prosub.params[
                Object.keys(this.prosub.params)?.find((key) => key?.toLowerCase() === "tp3")
              ]?.toLowerCase() || this.getDataFromLocalStorageOrCookie("TP3")
            : this.getDataFromLocalStorageOrCookie("TP3");
        let t_id =
          this.prosub && this.prosub?.params
            ? this.prosub?.params?.T_ID
              ? this.prosub.params.T_ID?.toLowerCase()
              : this.prosub.params[
                Object.keys(this.prosub.params)?.find((key) => key?.toLowerCase() === "t_id")
              ]?.toLowerCase() || this.getDataFromLocalStorageOrCookie("T_ID")
            : this.getDataFromLocalStorageOrCookie("T_ID");
        let other =
          this.prosub && this.prosub.params && this.prosub.params.Other
            ? this.prosub.params.Other?.toLowerCase()
            : this.safeLocalStorage.getItem("Other")
              ? this.safeLocalStorage.getItem("Other")
              : this.getCookie("Other")
                ? this.getCookie("Other")
                : "";
        return this.angularFireFunctions.httpsCallable("fourg_trigger")({
          prosub_id: prosub_id,
          userId: userId,
          Other: other,
          TP1: tp1,
          TP2: tp2,
          TP3: tp3,
          T_ID: t_id,
        });
      }));

  }

  // 4c flow
  check4cFlow(prosub_id) {
    return timer(0, 250).pipe(
      switchMap(() => this.authService.getUid$("AuthPartialRegService")),
      filter((userId) => !!userId),
      take(1),
      timeout(10000),
      switchMap((userId) => {
        let tp1 =
          this.prosub && this.prosub?.params && this.prosub?.params?.TP1
            ? this.prosub.params.TP1?.toLowerCase()
            : this.safeLocalStorage.getItem("TP1")
              ? this.safeLocalStorage.getItem("TP1")
              : this.getCookie("TP1")
                ? this.getCookie("TP1")
                : "";
        let tp2 =
          this.prosub && this.prosub?.params && this.prosub?.params?.TP2
            ? this.prosub.params.TP2?.toLowerCase()
            : this.safeLocalStorage.getItem("TP2")
              ? this.safeLocalStorage.getItem("TP2")
              : this.getCookie("TP2")
                ? this.getCookie("TP2")
                : "";
        let tp3 =
          this.prosub && this.prosub?.params
            ? this.prosub?.params?.TP3
              ? this.prosub.params.TP3?.toLowerCase()
              : this.prosub.params[
                Object.keys(this.prosub.params)?.find((key) => key?.toLowerCase() === "tp3")
              ]?.toLowerCase() || this.getDataFromLocalStorageOrCookie("TP3")
            : this.getDataFromLocalStorageOrCookie("TP3");
        let t_id =
          this.prosub && this.prosub?.params
            ? this.prosub?.params?.T_ID
              ? this.prosub.params.T_ID?.toLowerCase()
              : this.prosub.params[
                Object.keys(this.prosub.params)?.find((key) => key?.toLowerCase() === "t_id")
              ]?.toLowerCase() || this.getDataFromLocalStorageOrCookie("T_ID")
            : this.getDataFromLocalStorageOrCookie("T_ID");
        let other =
          this.prosub && this.prosub?.params && this.prosub?.params?.Other
            ? this.prosub.params.Other?.toLowerCase()
            : this.safeLocalStorage.getItem("Other")
              ? this.safeLocalStorage.getItem("Other")
              : this.getCookie("Other")
                ? this.getCookie("Other")
                : "";
        // return this.angularFireFunctions.httpsCallable("fourc_paymeent")({
        return this.getV2CloudFunction("fourcpaymentv2", {
          prosub_id: prosub_id,
          userId: userId,
          Other: other,
          TP1: tp1,
          TP2: tp2,
          TP3: tp3,
          T_ID: t_id,
        }).pipe(
          catchError((error) => {
            // Check if this is an IndexedDB corruption error
            if (this.indexedDBRecovery.isIndexedDBCorruptionError(error)) {
              this.authService.errorHandlerLogs('IndexedDB corruption detected in check4cFlow:', error);
              this.indexedDBRecovery.handleCorruptionAndReload().catch(err => {
                this.authService.errorHandlerLogs('Failed to recover from IndexedDB corruption:', err);
              });
            }
            // Re-throw the error for normal error handling
            return throwError(error);
          })
        );
      }));
  }

  // Set cookie in given name and days
  setCookie(name, value, days) {
    var expires = "";
    if (days) {
      var date = new Date();
      date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
      expires = "; expires=" + date.toUTCString();
    }
    document.cookie = name + "=" + (value || "") + expires + "; path=/";
  }

  getTruevoDetails(): void {
    try {
      this.getAngularFireStore(`/general_modules/p_type/list/22`, "snapshotChanges")
        .pipe(
          map((res: Data) => {
            return res?.payload?.data();
          }),
          catchError(() => of(null))
        )
        .subscribe(
          (value) => {
            this.truevoDetails$.next(value);
          },
          (err) => {
            return err;
          }
        );
    } catch (error) {
      return error;
    }
  }

  // Get data from Angular firestore
  // we already move
  getAngularFireStore(url: string, method: string): Observable<any> {
    // Gate every Firestore read on the custom token being ready to prevent
    // "Missing or insufficient permissions" race condition (LOTTOSOCIAL-PWA-1MED).
    return this.authStateService.customTokenReady$.pipe(
      take(1),
      switchMap(() => this.catchService.isOnline$),
      switchMap((isOnline) => {
        if (!isOnline) {
          this.authService.errorHandlerLogs("No internet connection.");
          return of(null); // Return a null observable if offline
        }
        const docRef = doc(this.firestore, url); // Reference the Firestore document
        if (method === "get") {
          // Using `getDoc()` for fetch operations
          return from(getDoc(docRef)).pipe(this.catchService.retryWhen, this.catchService.catchOfflineError);
        }
        if (method === "snapshotChanges") {
          // Using `onSnapshot()` for real-time document listening
          return new Observable((subscriber) => {
            const unsubscribe = onSnapshot(
              docRef,
              (snapshot) => {
                subscriber.next(snapshot.data()); // Emit document data
              },
              (error) => {
                subscriber.error(error); // Handle errors in real-time updates
              }
            );
            return () => unsubscribe(); // Clean up subscription when complete
          }).pipe(this.catchService.retryWhen, this.catchService.catchOfflineError);
        }
        this.authService.errorHandlerLogs("Invalid method provided.");
        return of(null); // Return a null observable for unsupported methods
      }),
      shareReplay(1), // Cache the response for reuse
      catchError((error) => {
        this.authService.errorHandlerLogs("Error in getAngularFireStore:", error);
        return of(null); // Return null on error to avoid breaking the chain
      })
    );
  }

  /**
   * It sets a cookie with the name "cje" and the value of the variable "value" and sets the expiration
   * time to one year.
   * @param {any} value - The value of the cookie.
   */
  setCJCookie(value: any) {
    var cookieName = "cje";
    var cookieValue = value;
    var expirationTime = 86400 * 365;
    expirationTime = expirationTime * 1000;
    var date = new Date();
    var dateTimeNow = date.getTime();
    date.setTime(dateTimeNow + expirationTime);
    document.cookie =
      cookieName +
      "=" +
      cookieValue +
      "; SameSite=None; Secure; expires=" +
      date.toUTCString() +
      "; path=/; domain=." +
      location.hostname.replace(/^www\./i, "");
  }

  updatePartialMsgRequest(smsPref, emailPref, userId) {
    try {
      // V2
      return this.getV2CloudFunction(
        "postasynceventsv2",
        this.requestBodyService.updatePrefrenceSetting(smsPref, emailPref, userId)
      );
      // V1
      /* const api = this.angularFireFunctions.httpsCallable("postAsyncEvents");
      return api(this.requestBodyService.updatePrefrenceSetting(smsPref, emailPref, userId)); */
    } catch (error) {
      return error;
    }
  }

  // Get constent for Prize Draw coachMark
  coachMarkData() {
    return this.getAngularFireStore("/Global_lookup/onboarding/tooltips/prize_draw", "snapshotChanges").pipe(
      map((res: Data) => res?.payload?.data())
    );
  }

  // Function to fetch currency and language based on country code from Firebase document
  getCurrencyAndLanguageList$ = this.getAngularFireStore(`/general_modules/countries`, "snapshotChanges").pipe(
    map((data: Data) => JSON.parse(JSON.stringify(data?.payload?.data()))),
    shareReplay(1),
    catchError(() =>
      of({
        GB: {
          currency: "GBP",
          language: "en",
        },
      })
    )
  );

  /* Getting the currency data from the firestore and converting it to JSON. */
  getActiveCurrencyList$ = this.getAngularFireStore(`/general_modules/currency`, "snapshotChanges").pipe(
    map((data: Data) => {
      let currenciesResponse = data?.payload?.data() ? JSON.parse(JSON.stringify(data.payload.data())) : null;
      return currenciesResponse?.details?.filter((currency) => currency.status === true);
    }),
    shareReplay(1),
    filter((data: Data) => data && data?.status === true)
  );

  // This methode has been use to open auto Show inbox messages
  openAutoShowInbox(message: Data) {

    // Determine the limb ID to use based on the condition
    const limbId = message.limb_id === message.id ? message.limb_id : message.id;
    // Check if the message template is "popup" and if it hasn't been viewed and a custom check passes
    if (message.template_name === "popup" && !message?.viewed && this.customCheck) {
      // Set custom check to false to prevent multiple popups
      this.customCheck = false;
      // Show the large information component as a popup
      const notification = this.matDialog.open(NotificationPopComponent, {
        panelClass: "npop-size",
        backdropClass: "npop-backdrop-color",
        data: message,
      });

      // Subscribe to the afterClosed event of the notification popup
      notification?.afterClosed()?.subscribe(() => {
        // Set custom check back to true for future popups
        this.autoShowInboxSubject.next(true);
        this.customCheck = true;
        return this.authService.getUid$("AuthPartialRegService").pipe(
          switchMap((userId) => {
            if (!userId || !limbId) {
              this.authService.errorHandlerLogs("Invalid User ID or Limb ID.");
              return;
            }
            const path_id = "dQ2JLcXGOr5fbVp8gyIf";
            const doc_data = { viewed: true, view_count: (message?.view_count || 0) + 1 };
            const dv = { docId: limbId };
            const method = "update";

            this.authService.getV2CloudFunction("updatefirestoredoc", {
              path_id,
              doc_data,
              dv,
              method,
            }).subscribe({
              error: (error) => {
                this.authService.errorHandlerLogs("Error updating viewed status:", error);
                return error;
              }
            });
            return of(userId) || of(null);
          }));
      });
    } else if (message?.template_name === "top_box") {
      //show informational small component
      const notification = this.matDialog.open(TopNotificationComponent, {
        panelClass: "opnotification-size",
        backdropClass: "opnotification-backdrop-color",
        data: message,
      });

      notification?.afterClosed()?.subscribe(() => {
        this.autoShowInboxSubject.next(true);
      });
    } else if (message?.template_name === "bottom_box_with_icon") {
      //show promotional medium suger component
      const bottomSheetRef = this.bottomSheet.open(SugerDropComponent, {
        panelClass: "suger-drop-bottomsheet",
        data: { update: true, message: message },
        hasBackdrop: true,
        backdropClass: "about-product-dialog-backdrop",
      });

      bottomSheetRef.afterDismissed().subscribe(() => {
        this.autoShowInboxSubject.next(true);
      });
    } else if (message?.template_name === "full_screen") {
      //show promotional standard component
      const standardPromo = this.bottomSheet.open(StandardPromotionComponent, {
        panelClass: "std-promotion-bottomsheet",
        data: { update: true, message: message },
        hasBackdrop: true,
        backdropClass: "about-product-dialog-backdrop",
      });
      standardPromo?.afterDismissed()?.subscribe(() => {
        // Set custom check back to true for future popups
        this.autoShowInboxSubject.next(true);
        return this.authService.getUid$("AuthPartialRegService").pipe(
          switchMap((userId) => {
            if (!userId || !message?.id) {
              this.authService.errorHandlerLogs("Invalid User ID or Message ID.");
              return;
            }
            const path_id = "dQ2JLcXGOr5fbVp8gyIf";
            const doc_data = {
              auto_show: false,
              viewed: true,
              view_count: (message?.view_count || 0) + 1,
            };
            const dv = { docId: message.id };
            const method = "update";

            this.authService.getV2CloudFunction("updatefirestoredoc", {
              path_id,
              doc_data,
              dv,
              method,
            }).subscribe({
              error: (error) => {
                this.authService.errorHandlerLogs("Error updating inbox document:", error);
                return error;
              }
            });
            return of(userId) || of(null);
          }));

      });
    } else if (message?.template_name === "takeover") {
      //show takeover component
      const standardPromo = this.bottomSheet.open(TakeoverPageComponent, {
        panelClass: "std-promotion-bottomsheet",
        data: { update: true, message: message },
        hasBackdrop: true,
        backdropClass: "about-product-dialog-backdrop",
      });

      standardPromo?.afterDismissed().subscribe(() => {
        this.autoShowInboxSubject.next(true);
      });
    } else if (message.template_name === "bottom_box") {
      const bottomSheetRef = this.bottomSheet.open(InformStripComponent, {
        panelClass: "promotional-small-bottomsheet",
        data: { update: true, message: message },
        hasBackdrop: true,
        backdropClass: "about-product-dialog-backdrop",
      });

      bottomSheetRef.afterDismissed().subscribe(() => {
        this.autoShowInboxSubject.next(true);
      });
    }
  }

  checkPushFreeLinesIssues(userId) {
    try {
      if (!userId) {
        return of(false); // Return Observable with default value
      }

      // No trailing slash. Use get() so forkJoin completes quickly — snapshotChanges
      // on a bad path can stall for minutes, then show claim copy on a new browser/device.
      const url = `/users/${userId}/in_app_notification/status`;
      return this.getAngularFireStore(url, "get").pipe(
        map((res: Data) => {
          // get() emits DocumentSnapshot; snapshotChanges emits plain data
          const responseData =
            res && typeof res.data === "function" ? res.data() : res;
          return responseData?.push_notification_syndicate_issued === true;
        }),
        catchError((error) => {
          this.authService.errorHandlerLogs("Firebase error in checkPushFreeLinesIssues:", error);
          return of(false); // Return Observable with default value on error
        })
      );
    } catch (error) {
      this.authService.errorHandlerLogs("Exception in checkPushFreeLinesIssues:", error);
      return of(false); // Return Observable with default value on exception
    }
  }

  checkOneSignalPushNotification(userId: string): Observable<boolean> {
    if (!userId) {
      return of(false);
    }

    const url = `users/${userId}/onesignal-push-notifications/details`;
    const userFingerPrintId =
      CommonVariable.FingerprintJSConst || this.safeLocalStorage.getItem("fingerSalt");

    return forkJoin({
      pushTokens: this.getAngularFireStore(url, "snapshotChanges").pipe(
        take(1),
        map((data: Data) => {
          return data ?? null;
        }),
        catchError(() => of(null))
      ),
      userPref: this.accountService.getUserSpecificPreferences().pipe(
        take(1),
        catchError(() => of(null))
      ),
      pushMeta: this.authService.getPushNotificationData().pipe(
        take(1),
        catchError(() => of(null))
      ),
      // Account-level prize flag (device-independent). Cap wait so a stuck read
      // cannot delay the banner for minutes and flash claim copy on a new device.
      issued: this.checkPushFreeLinesIssues(userId).pipe(
        take(1),
        timeout({ each: AuthPartialRegService.PUSH_ISSUED_FLAG_TIMEOUT_MS }),
        catchError((error) => {
          this.authService.errorHandlerLogs(
            "Push issued-flag read timed out or failed",
            error
          );
          return of(false);
        })
      ),
    }).pipe(
      map(({ pushTokens, userPref, pushMeta, issued }: { pushTokens: any; userPref: any; pushMeta: any; issued: boolean }) => {
        // Prize already issued on this account → claimed on every device/browser
        if (issued === true) {
          return true;
        }

        const tokens = pushTokens?.token_details ?? [];
        const isPushEnabled = userPref?.preferences_settings?.push;
        if (pushMeta?.push_pref !== undefined) {
          this.authService.push_pref.set(pushMeta.push_pref ?? true);
        }
        const activatedDevices = this.authService.getPushDeviceArrays(pushMeta).activatedDevices;
        // Match fingerprintId with userFingerPrintId

        const matchedDevice = activatedDevices.some(
          (data: Data) =>
            data?.fingerprintId === userFingerPrintId &&
            data?.activated === true
          );
        const isPushAct = matchedDevice && !isPushEnabled ? true : false;
        const match = tokens.find((x: Data) => {
          const hasFingerprint = x.fingerprint_id === userFingerPrintId;
          if (hasFingerprint && x.subscription_status === true) {
            // Push already active → No inline push or reward
            return false;
          }

          if (hasFingerprint && x.subscription_status === false) {
            // Already checked → Show alternative content
            return true;
          }

          if (!x.fingerprint_id) {
            // Token without fingerprint is not proof this account claimed the prize.
            // Never-awarded users must still see claim copy (isFreeLineClaimed=false).
            return false;
          }

          return false;
        });
        const result = !!match || isPushAct;
        return result;
      }),
      catchError((error) => {
        this.errorHandler.logError("Error in checkOneSignalPushNotification:", error);
        return of(false);
      })
    );
  }

  assignFreebieVoucher() {
    try {
      if (this.freebieVoucherAssigned === false) {
        this.freebieVoucherAssigned = true;
        return this.getV2CloudFunction("assignfreebievoucher", null);
      }
    } catch (error) {
      return error;
    }
  }

  // This method is used to open the new inbox template
  openTemplate(message: Data) {
    try {
      if (!message) return;
      this.bottomSheet.open(InboxTemplateComponent, {
        panelClass: "inbox-main-wrapper",
        data: message,
      });
    } catch (error) {
      return error;
    }
  }

  // Get inline Notification data for users
  public userInlineNotification(): Observable<any> {
    return this.catchService.isOnline$.pipe(
      switchMap((isOnline) => {
        if (!isOnline) {
          this.authService.errorHandlerLogs("No internet connection.");
          return of([]); // Return empty array if offline
        }

        return this.authService.getUid$("AuthPartialRegService").pipe(
          switchMap((userId) => {
            if (!userId) {
              return of([]);
            }

            const inlineNotificationCollectionRef = collection(
              this.firestore,
              `users/${userId}/inline_notification`
            );

            // Defense-in-depth gate (LOTTOSOCIAL-PWA-1MEE)
            return this.authStateService.customTokenReady$.pipe(
              take(1),
              switchMap(() => new Observable((subscriber) => {
                const unsubscribe = onSnapshot(
                  inlineNotificationCollectionRef,
                  (snapshot) => {
                    const actions = snapshot.docs.map((doc) => ({
                      id: doc.id,
                      ...doc.data(),
                    }));
                    subscriber.next(actions);
                  },
                  (error) => subscriber.error(error)
                );
                return () => unsubscribe(); // proper cleanup
              }).pipe(
                this.catchService.retryWhen,
                this.catchService.catchOfflineError,
                catchError((error) => {
                  this.authService.errorHandlerLogs("Error in userInlineNotification:", error);
                  return of([]); // Return an empty array to prevent errors from propagating
                })
              ))
            );
          })
        );
      }),
      shareReplay(1),
      map((actions: any[]) => this.snapShotData(actions)),
      catchError((error) => {
        this.authService.errorHandlerLogs("Error in userInlineNotification:", error);
        return of([]); // Return an empty array in case of an error
      })
    );
  }

  /**
   * It takes an array of DocumentChangeAction objects and returns an array of objects with the id and
   * data properties
   * @param {DocumentChangeAction<unknown>[]} actions -
   * import("@angular/fire/compat/firestore").DocumentChangeAction<unknown>[]
   * @returns An array of objects.
   */
  private snapShotData(actions: import("@angular/fire/compat/firestore").DocumentChangeAction<unknown>[]): any[] {
    return actions?.map((a: Data) => {
      const data = a?.payload?.doc?.data() as any;
      const id = a?.payload?.doc?.id;
      return { id, ...data };
    });
  }
  // check potential winnings to be shown in syndicate page
  checkPotentialWinnings() {
    try {
      return this.getV2CloudFunction("updatepotentialjackpot", null);
    } catch (error) {
      return error;
    }
  }

  // find sms from support_outgoing_messages collection
  // Uses polling via getDocs() instead of a persistent onSnapshot listener to avoid
  // a Firestore SDK 11.10.0 bug (internal assertion ve=-1) that fires when the
  // WebChannel reconnects while a real-time listener is being registered (LOTTOSOCIAL-PWA-1MZ5).
  checkSmsDeliveryStatus(to: string): Observable<any> {
    const supportMessageCollectionRef = query(
      collection(this.firestore, "support_outgoing_messages"),
      where("to", "==", to)
    );

    // Poll every 3 seconds instead of holding a persistent WebChannel listener
    return timer(0, 3000).pipe(
      switchMap(() =>
        from(getDocs(supportMessageCollectionRef)).pipe(
          map((snapshot) =>
            snapshot.docs.map((d) => ({ id: d.id, ...d.data() }))
          ),
          catchError((error) => {
            this.authService.errorHandlerLogs("Error checking SMS delivery status:", error);
            return of([]);
          })
        )
      )
    );
  }

  // To send the post message to the parent window to close the iframe
  // Used in 4b flow for special case based on 'iframe_close_postapi' node in prosub data
  public closeIframePostMessage(): void {
    try {
      // Check if we're actually in an iframe before attempting to send postMessage
      if (window.self !== window.top && window.parent) {
        // Use wildcard origin to safely communicate across all iframe contexts (same-origin and cross-origin)
        // This avoids SecurityError from attempting to access cross-origin window.parent.location
        window.parent.postMessage({ action: "closeIframe" }, "*");
        console.debug("[closeIframePostMessage] postMessage sent to parent window");
      }
    } catch (error) {
      // Only catch errors from postMessage operation itself
      this.authService.errorHandlerLogs("[closeIframePostMessage] Error sending postMessage:", error);
    }
  }

  // Partial registration URL Normalization
  public normalizeQueryParams(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return; // Exit safely during SSR
    }

    const url = new URL(window.location.href);
    const params = new URLSearchParams(url?.search || "");

    let changed = false;
    const lowerCased = new Map<string, string>();

    for (const [key, value] of params.entries()) {
      const lowerKey = key.toLowerCase();
      if (!lowerCased.has(lowerKey)) {
        lowerCased.set(lowerKey, value);
      } else {
        changed = true;
      }

      if (key !== lowerKey) changed = true;
    }

    if (changed) {
      // Construct URLSearchParams from array to avoid issues with sealed/frozen objects
      // This prevents the "Properties can only be defined on Objects" error when
      // the framesv2.min.js library seals the URLSearchParams prototype
      const newParams = new URLSearchParams(Array.from(lowerCased.entries()));
      const cleanUrl = `${url.pathname}?${newParams.toString()}`;
      this.router.navigateByUrl(cleanUrl, { replaceUrl: true });
    }
  }
}
